import type { FC } from "react";

import classNames from "classnames";

import type { SimplifiedMilestone } from "../../LoyaltyReferralStats.types";
import { ProgressBarChunk, ProgressBarMilestone, ProgressBarRewardBadge } from "../index";

import styles from "./ReferralStatsProgressBar.module.scss";

export type ReferralStatsProgressBarProps = {
  userCurrentReferralNumber: number;
  milestones: SimplifiedMilestone[];
  progressBarText?: string;
  milestonesText?: string;
  isMobileOrTablet: boolean;
};

export const ReferralStatsProgressBar: FC<ReferralStatsProgressBarProps> = ({
  userCurrentReferralNumber,
  milestones,
  progressBarText,
  milestonesText,
  isMobileOrTablet,
}) => {
  const getMilestoneState = (index: number) => {
    const milestone = milestones[index];
    const prevMilestone = milestones[index - 1] ?? { referralNumber: 0, mileStonePointsValue: 0 };

    const isFirst = index === 0;
    const isLast = index === milestones.length - 1;

    const isFirstChunkDone =
      prevMilestone.referralNumber < userCurrentReferralNumber || milestone.referralNumber <= userCurrentReferralNumber;
    const isSecondChunkDone = milestone.referralNumber <= userCurrentReferralNumber;
    const isMarkVisible = !isSecondChunkDone && userCurrentReferralNumber > prevMilestone.referralNumber;
    const isCurrentNotStarted = userCurrentReferralNumber === milestone.referralNumber;
    const isFilledMobile = userCurrentReferralNumber >= milestone.referralNumber;
    const isFirstInProgress =
      isFirst &&
      userCurrentReferralNumber !== prevMilestone.referralNumber &&
      userCurrentReferralNumber < milestone.referralNumber;

    const isProgressBarTextVisible = progressBarText && isFirst;

    return {
      milestone,
      prevMilestone,
      isFirst,
      isLast,
      isFirstChunkDone,
      isSecondChunkDone,
      isMarkVisible,
      isProgressBarTextVisible,
      isCurrentNotStarted,
      isFilledMobile,
      isFirstInProgress,
    };
  };

  if (!milestones || milestones.length <= 1) {
    return null;
  }

  return (
    <div className={classNames(styles.progressBarWrapper, "dark-theme")} data-testid="referralStatsProgressBar">
      {milestones.map((_, index) => {
        const {
          milestone,
          isFirst,
          isLast,
          isFirstChunkDone,
          isSecondChunkDone,
          isMarkVisible,
          isProgressBarTextVisible,
          isCurrentNotStarted,
          isFilledMobile,
          isFirstInProgress,
        } = getMilestoneState(index);

        return (
          <div key={index} className={styles.progressBarSection}>
            {isProgressBarTextVisible && <p className={styles.progressBarText}>{progressBarText}</p>}
            <ProgressBarMilestone
              milestoneText={milestone.referralNumber}
              isDone={isSecondChunkDone}
              isLast={isLast}
              className={classNames({ [styles.progressBarElementAdjusted]: !isLast })}
            />
            <div className={styles.progressBarChunkWrapper}>
              <ProgressBarChunk
                isFirst={isFirst}
                isFilled={isMobileOrTablet ? isFilledMobile : isFirstChunkDone}
                isLast={isMobileOrTablet && isLast}
                className={classNames({
                  [styles.partiallyDoneMobile]: isMobileOrTablet && isCurrentNotStarted && !isLast,
                  [styles.partiallyDoneMobileFirst]: isMobileOrTablet && isFirstInProgress,
                })}
              />
              {isMarkVisible && (
                <ProgressBarMilestone
                  isDone={true}
                  isLast={isLast}
                  milestoneText={userCurrentReferralNumber}
                  className={classNames(styles.progressBarMiddleMileStone, {
                    [styles.progressBarMiddleMileStoneMobileFirst]: isMobileOrTablet && isFirstInProgress,
                  })}
                />
              )}
              {!isMobileOrTablet && <ProgressBarChunk isLast={isLast} isFilled={isSecondChunkDone} />}
            </div>
            <ProgressBarRewardBadge
              isDone={isSecondChunkDone}
              mileStoneHeading={milestone.mileStonePointsValue}
              milestonesText={milestonesText ?? ""}
              className={classNames({ [styles.progressBarElementAdjusted]: !isLast })}
              isLast={isLast}
            />
          </div>
        );
      })}
    </div>
  );
};
